import { useQuery } from "@apollo/client";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CHECK_SALON_AI_AVAILABILITY_QUERY } from "@/graphql/google-business";
import { useAiReviewReply } from "../hooks/use-ai-review-reply";
import { useGoogleReviewReply } from "../hooks/use-google-review-reply";

interface GoogleReviewReplyDialogProps {
  reviewId: string;
  reviewerName: string;
  existingReply: string | null;
  salonId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_LENGTH = 4000;

export function GoogleReviewReplyDialog({
  reviewId,
  reviewerName,
  existingReply,
  salonId,
  open,
  onOpenChange,
}: GoogleReviewReplyDialogProps) {
  const { t } = useTranslation("settings");
  const { submitReply, submitting, removeReply, removing } = useGoogleReviewReply();
  const { generate, generating } = useAiReviewReply();
  const [text, setText] = useState(existingReply ?? "");

  const { data: aiAvailability } = useQuery(CHECK_SALON_AI_AVAILABILITY_QUERY, {
    variables: { salonId },
    skip: !open,
  });

  const isEditing = Boolean(existingReply);
  const isBusy = submitting || removing || generating;
  const showAiButton =
    aiAvailability?.checkSalonAiAvailability?.hasCredits &&
    aiAvailability?.checkSalonAiAvailability?.canUseAi;

  useEffect(() => {
    if (open) {
      setText(existingReply ?? "");
    }
  }, [open, existingReply]);

  const handleAiGenerate = async () => {
    const suggested = await generate(reviewId);
    if (suggested) {
      setText(suggested);
    }
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const result = await submitReply(reviewId, trimmed);
    if (result) {
      onOpenChange(false);
    }
  };

  const handleDelete = async () => {
    const result = await removeReply(reviewId);
    if (result) {
      onOpenChange(false);
      setText("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("reviews.replyDialog.title", { name: reviewerName })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("reviews.replyDialog.placeholder")}
            rows={5}
            maxLength={MAX_LENGTH}
            disabled={isBusy}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {showAiButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAiGenerate}
                  disabled={isBusy}
                  className="px-2 text-violet-600 hover:text-violet-700"
                >
                  {generating ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1 h-3 w-3" />
                  )}
                  {text.trim()
                    ? t("reviews.replyDialog.regenerate")
                    : t("reviews.replyDialog.generateAi")}
                </Button>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {text.length}/{MAX_LENGTH}
            </span>
          </div>
        </div>
        <DialogFooter className="items-center sm:justify-between">
          <div>
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={isBusy}
                className="text-destructive hover:text-destructive"
              >
                {removing ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="mr-1 h-3 w-3" />
                )}
                {t("actions.delete")}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isBusy}>
              {t("actions.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isBusy || !text.trim() || text.trim() === existingReply}
            >
              {submitting && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              {isEditing ? t("actions.save") : t("reviews.actions.reply")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
